import { Box, Link } from '@flos/react-ui';
import ProductAndPriceList from './ProductAndPriceList';
import CustomerBenefit from '../../../shared_components/CustomerBenefit/CustomerBenefit';
import TotalPrice from '../../../shared_components/TotalPrice/TotalPrice';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { QUOTED } from '../../../recoil/types/types';
import { jobAtom } from '../../../recoil/atoms/jobAtom';
import { paymentFrequencyMap, periodicityUnit } from '../../../utils/currencyUtil';
import ShoppingBasketGovernmentFee from '../../ShoppingBasketPage/components/ShoppingBasketGovernmentFee';
import { priceTaxAtom } from '../../../recoil/atoms/priceTaxAtom';
import { useRemoveApprovedTimestampAndReminderHook } from '../../../hooks/useRemoveApprovedTimestampAndReminderHook';
import { useHippoText } from '../../../hooks/useHippoText';
import useWizardNavigation from '../../../hooks/useWizardNavigation';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';
import { filterGovernmentFees } from '../../ShoppingBasketPage/utils/ShoppingBasketUtil';
import { businessFlowSelector } from '../../../recoil/selectors/getBusinessFlowSelector';
import { useAsyncRecoilValue } from '../../../hooks/useAsyncRecoilValue';
import { notUpdatedCoverablesAtom } from '../../../recoil/atoms/notUpdatedCoverablesAtom';
import { checkoutProgressAtom } from '../../../recoil/atoms/checkoutProgressAtom';

const CheckoutSidebar = (): React.JSX.Element => {
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const { isOpcJob } = jobData;
    const selectedPaymentFrequency =
        jobData.selectedPaymentFrequency ?? paymentFrequencyMap.monthly;
    const isPaymentYearly = selectedPaymentFrequency === paymentFrequencyMap.annual;
    const resetNotUpdatedCoverables = useResetRecoilState(notUpdatedCoverablesAtom);
    const resetCheckoutProgress = useResetRecoilState(checkoutProgressAtom);
    const [priceTaxData] = useAsyncRecoilValue(priceTaxAtom);
    const filteredGovernmentFees = filterGovernmentFees(priceTaxData?.annualTaxes ?? []);
    const accountHolder = useRecoilValue(accountHolderAtom);
    const businessFlow = useRecoilValue(businessFlowSelector);

    const { removeApprovedTimestampAndReminder } = useRemoveApprovedTimestampAndReminderHook();
    const { wizardGotoStep } = useWizardNavigation();
    const getHippoText = useHippoText();

    const goBackToShoppingBasket = (): void => {
        void removeApprovedTimestampAndReminder().then(() => {
            setJobData({ ...jobData, jobStatus: QUOTED });
            resetNotUpdatedCoverables();
            resetCheckoutProgress();
            wizardGotoStep('ShoppingBasketPage');
        });
    };

    const isMPSBusinessFlow = businessFlow === 'MPS';

    const hippoTexts = {
        shoppingBasketHeader: !isMPSBusinessFlow
            ? getHippoText('DSS-COMMON-SHOPPING-BASKET-HEADER')
            : getHippoText('DSS-MPS-FLOW-CREATE-AGREEMENT-SECTION-POLICY-INFO-HEADER'),
        editShoppingBasketButton: getHippoText('DSS-CHECKOUT-EDIT-BASKET')
    };

    if (jobData.isCancelled) {
        return (
            <div className="u-flex-box u-flex-box--col">
                <Box padding="large" theme="transparent" className="u-shadow-none">
                    <div className="u-flex-box u-flex-box--space-between">
                        <p className="paragraph--small u-color-supplement-error-dark">
                            {getHippoText(
                                'DSS-MPS-FLOW-CREATE-AGREEMENT-SECTION-CANCELLED-POLICY-INFO-HEADER'
                            )}
                        </p>
                    </div>
                    <hr className="u-spacing-stack-none" />
                    <ProductAndPriceList
                        paymentFrequency={selectedPaymentFrequency}
                        isPaymentYearly={isPaymentYearly}
                        shouldHideDetails={true}
                    />
                </Box>
            </div>
        );
    }

    return (
        <div
            className={
                'u-flex-box u-flex-box--col u-spacing-inset-bottom-12 u-spacing-inset-bottom-none@xs'
            }
        >
            <Box padding={'large'} theme={'transparent'} className={'u-shadow-none'}>
                <div className={'u-flex-box u-flex-box--space-between'}>
                    <p className={'paragraph--small u-color-secondary-4-medium'}>
                        {hippoTexts.shoppingBasketHeader}
                    </p>
                    {!isMPSBusinessFlow &&
                        !accountHolder.isCVR &&
                        businessFlow !== 'OPC' &&
                        !isOpcJob && (
                            <Link
                                className="paragraph--small"
                                onClick={goBackToShoppingBasket}
                                theme={'logo'}
                            >
                                {hippoTexts.editShoppingBasketButton}
                            </Link>
                        )}
                </div>
                <hr className={'u-spacing-stack-none'} />
                <ProductAndPriceList
                    paymentFrequency={selectedPaymentFrequency}
                    isPaymentYearly={isPaymentYearly}
                />
                <hr className={'u-spacing-stack-m'} />
                <CustomerBenefit interactive={false} />
                <TotalPrice
                    totalPrice={
                        selectedPaymentFrequency === paymentFrequencyMap.monthly
                            ? priceTaxData?.monthlyPrice?.amount ?? 0
                            : priceTaxData?.yearlyPrice?.amount ?? 0
                    }
                    showYearlyPriceField={selectedPaymentFrequency === paymentFrequencyMap.monthly}
                    paymentFrequencyPostfix={periodicityUnit[selectedPaymentFrequency]}
                    isCVR={accountHolder.isCVR}
                />
            </Box>
            {filteredGovernmentFees.length > 0 && businessFlow !== 'OPC' && !jobData.isOpcJob && (
                <Box padding={'large'} theme={'transparent'} className={'u-shadow-none'}>
                    <ShoppingBasketGovernmentFee governmentFees={filteredGovernmentFees} />
                </Box>
            )}
        </div>
    );
};

export default CheckoutSidebar;
